import type { Step } from './state'

// 分頁推導的唯一來源。作答畫面、推進鈕的驗證範圍、頁碼與進度條全部讀這裡算出來的同一份結果 ——
// 以前元件的 currentPageSteps() 與 activeView 各切一次，靠一句「切法與 activeView 一致」的註解
// 維持不變量。漂移的後果是 next() 拿 A 頁的題目做驗證、使用者看的卻是 B 頁：一種從失敗訊息看
// 不出成因的症狀，而擋住它的只有那句註解。
//
// 消費端拿到的是結果不是原料：分頁只有這一處在切。完整的 steps 只以 sizingSteps 的形式出去，
// 而它只拿來撐高（見 render/active.ts 的 .answer-sizer），不參與分頁。

export interface Page {
  // 這一頁要渲染的題目（最後一頁不足額時就是剩下的那幾題）。
  steps: Step[]
  // 這一頁第一題在整份問卷裡的 0-based 序號 —— Q 編號是全域的（跨頁連號，與結果頁一致）。
  startIndex: number
  // clamp 後的頁索引。存起來的 pageIdx 可能越界：applySurvey 只在 survey.id 變時重置它，
  // 同一份問卷題數變少的 reapply 會留下超過範圍的舊值。
  pageIdx: number
  pageCount: number
  isLast: boolean
  // 這份問卷是不是一頁多題 —— 決定按鈕說「上一頁／下一頁」還是「上一題／下一題」。
  // 依每頁題數而非當頁題數：最後一頁只剩一題時措辭不該跟著跳回「題」。
  multi: boolean
  // inline 撐高層要渲染的全部題目（同一個陣列參照，不複製）；overlay 是 null。inline 的面板高度
  // 是「整份問卷最高那題」，而每次只畫當前題，所以得另外拿到全部題目才量得出來。overlay 的面板是
  // 固定高，不需要這一份，也不該多渲染一份。
  sizingSteps: Step[] | null
}

// 每頁題數。inline 恆 1；overlay 讀 questions_per_page，非正整數一律回 1（缺省 = 單題單頁）。
export function resolvePerPage(perPage: number | null | undefined, isOverlay: boolean): number {
  if (!isOverlay)
    return 1
  return typeof perPage === 'number' && Number.isInteger(perPage) && perPage >= 1 ? perPage : 1
}

export function derivePage(input: {
  steps: Step[]
  pageIdx: number
  questionsPerPage: number | null | undefined
  isOverlay: boolean
}): Page {
  const perPage = resolvePerPage(input.questionsPerPage, input.isOverlay)
  // 沒有題目時頁數仍算 1（steps 會是空陣列）—— 呼叫端據此決定畫什麼，不必另外處理 0 頁。
  const pageCount = Math.max(1, Math.ceil(input.steps.length / perPage))
  const pageIdx = Math.min(Math.max(0, input.pageIdx), pageCount - 1)
  const startIndex = pageIdx * perPage
  return {
    steps: input.steps.slice(startIndex, startIndex + perPage),
    startIndex,
    pageIdx,
    pageCount,
    isLast: pageIdx === pageCount - 1,
    multi: perPage > 1,
    sizingSteps: input.isOverlay ? null : input.steps,
  }
}
